import { FilterLinesIcon } from '@langchain/untitled-ui-icons';

import { ReactNode, useMemo, useState } from 'react';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/Popover';
import { Tooltip } from '@/design-system';
import { RunFacetField } from '@/types/schema';
import { useClickLogger } from '@/utils/datadog/useClickLogger';
import { cn } from '@/utils/tailwind';

import { parseCustomFilter } from '../utils/parseCustomFilter';
import { translateToCustomFilters } from '../utils/translateToCustomFilters';
import { DatasetSessionCompareFilterItem } from './DatasetSessionCompareFilterItem';
import { FeedbackFilter } from './SingleExperimentColumns/FeedbackColumns/FeedbackFilter';

function StatsFilters({
  facetsGroupedByKey,
  values,
  handleFilterChange,
}: {
  facetsGroupedByKey: Record<string, RunFacetField[]>;
  values: string[];
  handleFilterChange: (value: string) => void;
}) {
  return (
    <>
      <div className="flex items-center bg-secondary px-3 py-2 text-xs font-medium uppercase text-tertiary">
        Stats
      </div>
      <div className="pb-2 pt-1">
        {Object.entries(facetsGroupedByKey).map(([label, facets]) => {
          return (
            <div key={label}>
              <DatasetSessionCompareFilterItem
                label={label
                  .split('_')
                  .map((i) => i[0].toUpperCase() + i.slice(1))
                  .join(' ')}
                selected={values.includes(label)}
                onChange={handleFilterChange}
                value={label}
              />
              {facets.map((facet) => {
                return (
                  <DatasetSessionCompareFilterItem
                    isNested
                    key={facet.query}
                    label={facet.value}
                    n={facet.n}
                    selected={values.includes(facet.query)}
                    onChange={handleFilterChange}
                    value={facet.query}
                  />
                );
              })}
            </div>
          );
        })}
      </div>
    </>
  );
}

function FeedbackFilters({
  allFeedback,
  values,
  handleFilterChange,
  onChange,
  currentFilters,
  filtersWithoutFeedback,
  setFiltersWithoutFeedback,
}: {
  allFeedback: [string, RunFacetField[]][];
  values: string[];
  handleFilterChange: (value: string) => void;
  onChange: (value: string[]) => void;
  currentFilters: string[];
  filtersWithoutFeedback: Record<string, string[]>;
  setFiltersWithoutFeedback: (value: Record<string, string[]>) => void;
}) {
  return (
    <>
      <div className="flex items-center bg-secondary px-3 py-2 text-xs font-medium uppercase text-tertiary">
        Feedback
      </div>

      <div className="pb-2 pt-1">
        {allFeedback.map(([label, _]) => {
          return (
            <FeedbackFilter
              key={label}
              label={label}
              values={values}
              handleFilterChange={handleFilterChange}
              onChange={onChange}
              currentFilters={currentFilters}
              customFilters={filtersWithoutFeedback}
              setCustomFilters={setFiltersWithoutFeedback}
            />
          );
        })}
      </div>
    </>
  );
}

function FilterTrigger({ numActiveFilters }: { numActiveFilters: number }) {
  return (
    <Tooltip title="Filter" side="top">
      <div
        className={cn(
          'flex min-h-[25px] w-full shrink-0 cursor-pointer items-center gap-1 rounded border border-transparent p-1 py-0.5',
          'text-quaternary hover:bg-quaternary active:bg-secondary ui-open:bg-secondary',
          numActiveFilters > 0 &&
            'border border-brand-subtle bg-brand-tertiary text-brand-primary hover:bg-brand-secondary'
        )}
        data-testid="comparison-view-filter-button"
      >
        <FilterLinesIcon className="h-4 w-4" />

        {numActiveFilters > 0 && (
          <span className="flex items-center justify-center gap-1 rounded text-center text-xs leading-none text-brand-primary">
            {numActiveFilters}
          </span>
        )}
      </div>
    </Tooltip>
  );
}

export function DatasetSessionCompareFilter(props: {
  label?: ReactNode;
  currentFilters: string[]; // filters currently applied to the table
  onChange: (value: string[]) => void; // function to call when a filter is applied or removed
  facets: RunFacetField[]; // options to display in this dropdown
  hideClearButton?: boolean;
}) {
  const logClick = useClickLogger();
  const [filtersWithoutFeedback, setFiltersWithoutFeedback] = useState<
    Record<string, string[]>
  >(
    translateToCustomFilters(
      props.currentFilters.filter(
        (f) => f.includes('feedback_key') && f.includes('feedback_score')
      )
    )
  );

  const facetsGroupedByKey = useMemo(
    () =>
      props.facets.reduce<Record<string, RunFacetField[]>>((memo, item) => {
        if (item.key.startsWith('feedback_key')) {
          const key = ['feedback_key', item.value.split(' == ')[0]].join(':');
          memo[key] = [];
          for (const [k, v] of Object.entries(filtersWithoutFeedback)) {
            if (k === key) {
              for (const customFilter of v) {
                const parsedFilter =
                  parseCustomFilter(
                    customFilter,
                    item.value.split(' == ')[0]
                  ) ?? '';
                if (parsedFilter) {
                  memo[key].push({
                    key: k,
                    query: parsedFilter,
                    value: k,
                  });
                }
              }
            }
          }
          return memo;
        }

        const lst = memo[item.key] ?? [];
        lst.push(item);
        memo[item.key] = lst;
        return memo;
      }, {}),
    [props.facets, filtersWithoutFeedback]
  );

  const values = useMemo(
    () =>
      Object.entries(facetsGroupedByKey).flatMap(([_, facetsInGroup]) => {
        const result: string[] = [];

        for (const query of facetsInGroup.map((facet) => facet.query)) {
          if (props.currentFilters.includes(query)) {
            result.push(query);
          }
        }
        return result;
      }),
    [facetsGroupedByKey, props.currentFilters]
  );

  const handleFilterChange = (value: string) => {
    const added = !values.includes(value);

    let newValue = [...props.currentFilters];
    if (added) {
      if (value in facetsGroupedByKey) {
        // if add is a group, then add all of the filters in that group
        newValue.push(...facetsGroupedByKey[value].map((i) => i.query));
        const allInGroupSelected = facetsGroupedByKey[value].every((i) =>
          props.currentFilters.includes(i.query)
        );
        if (allInGroupSelected) {
          newValue = newValue.filter(
            (i) => !facetsGroupedByKey[value].map((i) => i.query).includes(i)
          );
        } else {
          newValue.push(...facetsGroupedByKey[value].map((i) => i.query));
        }
      } else {
        newValue.push(value);
      }
    } else {
      if (value in facetsGroupedByKey) {
        // if remove is a group, then remove all of the filters in that group (should never happen)
        newValue = newValue.filter(
          (i) => !facetsGroupedByKey[value].map((i) => i.query).includes(i)
        );
      } else {
        newValue = newValue.filter((i) => i !== value);
      }
    }

    props.onChange([...new Set(newValue)]);
  };

  const allFeedback = Object.entries(facetsGroupedByKey)
    .filter(([label]) => label.startsWith('feedback_key:'))
    .sort(([a], [b]) => {
      const aKey = a.slice('feedback_key:'.length);
      const bKey = b.slice('feedback_key:'.length);
      return aKey.localeCompare(bKey);
    });

  const statsFacets = Object.fromEntries(
    Object.entries(facetsGroupedByKey).filter(
      ([key]) => !key.startsWith('feedback_key')
    )
  );

  const shouldAutoFocus =
    allFeedback.length === 1 &&
    Object.values(facetsGroupedByKey)[0].length === 0;

  return (
    <Popover
      onOpenChange={(open) => {
        if (open) {
          logClick('filter_popover_open');
          // auto focus into the feedback filter input so the placeholder example text is shown
          if (shouldAutoFocus) {
            setFiltersWithoutFeedback({
              [Object.keys(facetsGroupedByKey)[0]]: [''],
            });
          }
        } else {
          logClick('filter_popover_close');
        }
      }}
    >
      <PopoverTrigger>
        <FilterTrigger
          numActiveFilters={
            Object.values(facetsGroupedByKey)
              .flat()
              .filter((facet) => props.currentFilters.includes(facet.query))
              .length
          }
        />
      </PopoverTrigger>
      <PopoverContent side="bottom" className="max-h-[500px] overflow-auto p-0">
        {props.label && (
          <div className="sticky top-0 z-10 max-w-full overflow-hidden text-ellipsis bg-background px-3 py-2">
            {props.label}
          </div>
        )}
        {allFeedback.length > 0 && (
          <FeedbackFilters
            allFeedback={allFeedback}
            values={values}
            handleFilterChange={handleFilterChange}
            onChange={props.onChange}
            currentFilters={props.currentFilters}
            filtersWithoutFeedback={filtersWithoutFeedback}
            setFiltersWithoutFeedback={setFiltersWithoutFeedback}
          />
        )}
        {Object.keys(statsFacets).length > 0 && (
          <StatsFilters
            facetsGroupedByKey={statsFacets}
            values={values}
            handleFilterChange={handleFilterChange}
          />
        )}
        {!props.hideClearButton && <ClearButton onChange={props.onChange} />}
      </PopoverContent>
    </Popover>
  );
}

function ClearButton({ onChange }: { onChange: (value: string[]) => void }) {
  return (
    <div className="sticky bottom-0 flex gap-2 bg-background p-3 pt-2">
      <button
        type="button"
        className="rounded-md border border-secondary px-1 py-0.5 text-xs text-tertiary "
        onClick={() => onChange([])}
      >
        Clear
      </button>
    </div>
  );
}
